/**
 * SDK-facing payloads — `/sdk/v1/*` (PLAN §1.2, `docs/CONTRACT.md` §4).
 */
import { z } from "zod";
import { attributes, changeNumber, contextKey, epochMillis, flagKey, segmentName, } from "./common.js";
import { evaluationReason, flagDefinition } from "./definition.js";
/** `GET /sdk/v1/definitions?since=<n>` */
export const definitionsResponse = z
    .object({
    since: changeNumber,
    till: changeNumber,
    flags: z.array(flagDefinition),
    /** Segment names referenced by any served definition. */
    segmentsUsed: z.array(segmentName),
    /** Flags deleted or archived since `since`; SDKs drop them from the cache. */
    removed: z.array(flagKey).default([]),
})
    .strict();
/** `GET /sdk/v1/memberships?key=<k>` — Split's mySegments. */
export const membershipsResponse = z
    .object({
    key: contextKey,
    segments: z.array(segmentName),
    till: changeNumber,
})
    .strict();
/** `GET /sdk/v1/segments/:name?since=` — server keys only. */
export const segmentResponse = z
    .object({
    name: segmentName,
    since: changeNumber,
    till: changeNumber,
    added: z.array(contextKey),
    removed: z.array(contextKey),
})
    .strict();
/** `POST /sdk/v1/evaluate` — server keys and the admin debugger. */
export const evaluateRequest = z
    .object({
    key: contextKey,
    attributes: attributes.optional(),
    flags: z.array(flagKey).max(500).optional(),
    /**
     * `true` records no impressions. Omitted or `false` records one impression
     * per evaluated flag that has a definition (CONTRACT §4.4).
     */
    dryRun: z.boolean().optional(),
})
    .strict();
/** Most contexts one `POST /sdk/v1/evaluate` batch may carry (CONTRACT §4.4). */
export const MAX_EVALUATE_CONTEXTS = 100;
export const evaluateContext = z
    .object({ key: contextKey, attributes: attributes.optional() })
    .strict();
/**
 * Batch form of `POST /sdk/v1/evaluate`: several contexts against the same
 * flags in one call. `dryRun: true` records no impressions; the default
 * records them exactly as the single form does.
 */
export const evaluateBatchRequest = z
    .object({
    contexts: z.array(evaluateContext).min(1).max(MAX_EVALUATE_CONTEXTS),
    flags: z.array(flagKey).max(500).optional(),
    dryRun: z.boolean().optional(),
})
    .strict();
export const evaluateResultEntry = z
    .object({
    flag: flagKey,
    treatment: z.string(),
    reason: evaluationReason,
    ruleId: z.string().optional(),
    config: z.string().optional(),
    changeNumber: changeNumber.optional(),
})
    .strict();
export const evaluateResponse = z
    .object({
    key: contextKey,
    till: changeNumber,
    results: z.array(evaluateResultEntry),
})
    .strict();
export const evaluateBatchResponse = z
    .object({
    till: changeNumber,
    /** One entry per request context, in request order. */
    results: z.array(z.object({ key: contextKey, evaluations: z.array(evaluateResultEntry) }).strict()),
})
    .strict();
/** `GET /sdk/v1/flags/:flag` — one flag's served definition (server keys only). */
export const flagDefinitionResponse = z
    .object({ till: changeNumber, definition: flagDefinition })
    .strict();
/** One impression as an SDK queues it. */
export const impression = z
    .object({
    flag: flagKey,
    key: contextKey,
    treatment: z.string().max(50),
    reason: evaluationReason,
    ruleId: z.string().max(64).optional(),
    changeNumber,
    time: epochMillis,
    /** Optional bucketing key when the SDK evaluated with a separate one. */
    bucketingKey: contextKey.optional(),
})
    .strict();
export const impressionsBatch = z
    .object({
    impressions: z.array(impression).min(1).max(1000),
    sdk: z.string().max(60).optional(),
    sdkVersion: z.string().max(40).optional(),
})
    .strict();
export const EVENT_TYPE_PATTERN = /^[a-zA-Z0-9][-_.:a-zA-Z0-9]{0,79}$/;
export const eventProperties = z.record(z.string().min(1).max(100), z.union([z.string().max(500), z.number().finite(), z.boolean(), z.null()]));
export const trackEvent = z
    .object({
    key: contextKey,
    eventType: z.string().regex(EVENT_TYPE_PATTERN),
    value: z.number().finite().optional(),
    properties: eventProperties.optional(),
    time: epochMillis,
})
    .strict();
export const eventsBatch = z
    .object({
    events: z.array(trackEvent).min(1).max(1000),
    sdk: z.string().max(60).optional(),
    sdkVersion: z.string().max(40).optional(),
})
    .strict();
/** Accepted-count body returned by both batch endpoints. */
export const batchAccepted = z
    .object({ accepted: z.number().int().nonnegative(), dropped: z.number().int().nonnegative() })
    .strict();
/** SSE payloads on `GET /sdk/v1/stream` (CONTRACT §4.7). */
export const STREAM_EVENTS = ["flags_changed", "segments_changed", "heartbeat"];
export const streamEventName = z.enum(STREAM_EVENTS);
export const streamPayload = z
    .object({
    till: changeNumber.optional(),
    time: epochMillis.optional(),
    /** Present on `segments_changed` when a single segment changed. */
    segment: segmentName.optional(),
})
    .strict();
//# sourceMappingURL=sdk.js.map